import Card from "components/card";
import React from "react";
import { MdArrowLeft, MdArrowRight, MdEdit } from "react-icons/md";
import { useGetClients } from "modules/article/api";
import { useGetWorkspaceStaff } from "modules/workspaces/api";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { FiSearch } from "react-icons/fi";

export type RowObjCerfa = {
  _id: string;
  totalCharge: number;
  fluidName: string;
  clientId: string;
  operatorId: string;
  createdAt: Date;
};

const columnHelper = createColumnHelper<RowObjCerfa>();

export default function CerfaTable(props: {
  tableData: RowObjCerfa[];
  searchName: string;
  setSearchName: any;
  currentPage: number;
  totalPage: number;
  hasNextPage: boolean;
  handleFetchNextPage: () => void;
  handleFetchPrevPage: () => void;
}) {
  const {
    tableData,
    handleFetchNextPage,
    hasNextPage,
    handleFetchPrevPage,
    totalPage,
    currentPage,
  } = props;
  const [sorting, setSorting] = React.useState<SortingState>([]);
  let defaultData = tableData;
  console.log(defaultData)

  const { data: clients } = useGetClients("", 500);
  const { data: operators } = useGetWorkspaceStaff(); // Assuming you have this hook
  const flatClients = React.useMemo(
    () => clients?.pages?.flatMap((page) => page.data) || [],
    [clients?.pages]
  );
  const flatOperators = operators;

  const clientIdToName = React.useMemo(
    () =>
      flatClients.reduce(
        (acc, client) => ({
          ...acc,
          [client._id]: client.name,
        }),
        {} as Record<string, string>
      ),
    [flatClients]
  );

  const operatorIdToName = React.useMemo(
    () =>
      flatOperators?.reduce(
        (acc, operator) => ({
          ...acc,
          [operator.id]: operator.name,
        }),
        {} as Record<string, string>
      ) || {},
    [flatOperators]
  );
  const columns = [
    columnHelper.accessor("clientId", {
      id: "Client",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          Client
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {clientIdToName[info.getValue()] || "-"}{" "}
          <span className="text-xs text-gray-400"></span>
        </p>
      ),
    }),
    columnHelper.accessor("operatorId", {
      id: "Operator",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          Operator
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {operatorIdToName[info.getValue()] || "-"}{" "}
          <span className="text-xs text-gray-400"></span>
        </p>
      ),
    }),
    columnHelper.accessor("createdAt", {
      id: "Date",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">Date</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {new Date(info.getValue()).toLocaleDateString()}{" "}
          <span className="text-xs text-gray-400"></span>
        </p>
      ),
    }),
    columnHelper.accessor("totalCharge", {
      id: "Total Charge",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          Total Charge
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue().toFixed(2) + " €"}{" "}
          <span className="text-xs text-gray-400"></span>
        </p>
      ),
    }),
    columnHelper.accessor("_id", {
      id: "actions",
      header: () => (
        <p className="flex justify-end text-sm font-bold text-gray-600 dark:text-white">
          MODIFIER
        </p>
      ),
      cell: (id) => (
        <div className="flex items-end justify-end">
          <a
            className="text-xl font-bold text-brand-500 dark:text-white"
            href={`/admin/cerfa/${id.getValue()}`}
          >
            <MdEdit />
          </a>
        </div>
      ),
    }),
  ];
  const table = useReactTable({
    data: defaultData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });
  return (
    <Card extra={"w-full h-full px-6 pb-6 sm:overflow-x-auto"}>
      <div className="relative flex items-center justify-between pt-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Cerfa
        </div>
        <div className="flex items-center gap-4">
          <div className="flex h-full items-center rounded-full bg-lightPrimary py-3.5 text-navy-700 dark:bg-navy-900 dark:text-white xl:w-[250px]">
            <p className="pl-3 pr-2 text-xl">
              <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
            </p>
            <input
              type="text"
              placeholder="Search..."
              className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white sm:w-fit"
              value={props.searchName}
              onChange={(e) => props.setSearchName(e.target.value)}
            />
          </div>
          <a
            className="cursor-pointer rounded-full bg-brand-500 p-0.5 text-white"
            href="/admin/cerfa/create"
          >
            <svg
              className="h-8 w-8 cursor-pointer"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
          </a>
        </div>
      </div>

      <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="!border-px !border-gray-400">
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer border-b-[1px] border-gray-200 pb-2 pr-4 pt-4 text-start"
                    >
                      <div className="items-center justify-between text-xs text-gray-200">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: "",
                          desc: "",
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table
              .getRowModel()
              .rows.slice(0)
              .map((row) => {
                return (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td
                          key={cell.id}
                          className="min-w-[150px] border-white/0 py-3  pr-4"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <div className="mt-5 flex items-center justify-end">
        <MdArrowLeft
          onClick={handleFetchPrevPage}
          className={`h-8 w-8 cursor-pointer ${
            currentPage - 1 > 0 ? "text-brand-500" : "text-gray-400"
          }`}
        />
        <p className="text-sm text-navy-700 dark:text-white">
          Page {currentPage} sur {totalPage}
        </p>
        <MdArrowRight
          onClick={handleFetchNextPage}
          className={`h-8 w-8 cursor-pointer ${
            hasNextPage ? "text-brand-500" : "text-gray-400"
          }`}
        />
      </div>
    </Card>
  );
}
